import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TableViewIcon from "@mui/icons-material/TableView";
import { alpha } from "@mui/material/styles";

import { Reddit as RedditIcon } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import InsightsIcon from "@mui/icons-material/Insights";
import ArticleIcon from "@mui/icons-material/Article";

import { getSubreddits, getRedditTasks } from "../../services/reddit_services";
import { getAllResultsCount } from "../../services/generic_services";
import RedditSubmissionCard from "../reddit/RedditSubmissionCard";
import KpiTile from "../results/KpiTile";
import FiltersPanel from "../results/FiltersPanel";

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const toCsv = (rows, columns) => {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(c.get(r))).join(","))
    .join("\n");
  return `﻿${header}\n${body}\n`;
};
const downloadBlob = (content, mime, filename) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const REDDIT_COLOR = "#FF4500";

// Recursively collect all comment bodies from the nested reply tree.
const flattenComments = (comments) => {
  const out = [];
  (comments || []).forEach((c) => {
    const body = (c.body || "").trim();
    if (body) out.push(body);
    if (Array.isArray(c.replies) && c.replies.length) {
      out.push(...flattenComments(c.replies));
    }
  });
  return out;
};

const SORT_FIELDS = [
  { value: "created_at", label: "Date" },
  { value: "subreddit", label: "Subreddit" },
  { value: "public_description", label: "Description" },
];

const inDateRange = (dateLike, range) => {
  if (range === "all" || !dateLike) return true;
  const ts = typeof dateLike === "number" ? dateLike * 1000 : new Date(dateLike).getTime();
  if (Number.isNaN(ts)) return true;
  const now = Date.now();
  const cutoffMs = { "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 }[range];
  return cutoffMs ? now - ts <= cutoffMs : true;
};

const RedditResults = () => {
  const [results, setResults] = useState([]);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [allSubreddits, setAllSubreddits] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    dateRange: "all",
    confidenceMin: 0,
    sources: {},
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        sort_by: sortBy,
        sort_direction: sortDirection,
        search: filters.search,
      };
      const checked = allSubreddits.filter((s) => filters.sources[s] !== false);
      if (allSubreddits.length > 0 && checked.length < allSubreddits.length) {
        params.subreddits = checked.join(",");
      }
      const [pageData, counts] = await Promise.allSettled([
        getSubreddits(params),
        getAllResultsCount(),
      ]);
      if (pageData.status === "fulfilled") setResults(pageData.value || []);
      if (counts.status === "fulfilled") setSubmissionsCount(counts.value?.submissions || 0);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortDirection, filters.search, filters.sources, allSubreddits]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch all tracked subreddits from tasks (one-time, gives complete list)
  useEffect(() => {
    (async () => {
      try {
        const tasks = await getRedditTasks();
        const subs = Array.from(new Set((tasks || []).map((t) => t.subreddit).filter(Boolean))).sort();
        setAllSubreddits(subs);
      } catch (e) {
        console.log("Failed to fetch Reddit subreddits", e);
      }
    })();
  }, []);

  const subredditSources = useMemo(
    () =>
      allSubreddits.map((sub) => ({
        key: sub,
        label: `r/${sub}`,
        color: REDDIT_COLOR,
        checked: filters.sources[sub] !== false,
      })),
    [allSubreddits, filters.sources]
  );

  const toggleSource = (key) => {
    setFilters((f) => ({
      ...f,
      sources: { ...f.sources, [key]: f.sources[key] === false },
    }));
    setPage(1);
  };

  const resetFilters = () =>
    setFilters({ search: "", dateRange: "all", confidenceMin: 0, sources: {} });

  const filtered = useMemo(() => {
    return results
      .filter((sub) => filters.sources[sub.subreddit] !== false)
      .map((sub) => ({
        ...sub,
        submissions: (sub.submissions || []).filter((s) => {
          if (!inDateRange(s.created_utc, filters.dateRange)) return false;
          const conf = typeof s.confidence === "number" ? s.confidence : 0;
          if (conf < filters.confidenceMin) return false;
          return true;
        }),
      }));
  }, [results, filters]);

  const kpis = useMemo(() => {
    const subredditsCount = results.length;
    const allSubs = results.flatMap((s) => s.submissions || []);
    const confidences = allSubs
      .map((s) => s.confidence)
      .filter((c) => typeof c === "number");
    const avgConf = confidences.length
      ? confidences.reduce((a, b) => a + b, 0) / confidences.length
      : null;
    return {
      total: submissionsCount,
      inView: allSubs.length,
      subreddits: subredditsCount,
      avgConf,
    };
  }, [results, submissionsCount]);

  const [exportAnchor, setExportAnchor] = useState(null);

  const exportVisible = useCallback((format) => {
    const date = new Date().toISOString().slice(0, 10);
    const base = `reddit-results-${date}`;
    if (format === "json") {
      downloadBlob(JSON.stringify(filtered, null, 2), "application/json", `${base}.json`);
    } else {
      // Flatten subreddit → submissions for CSV
      const flat = [];
      filtered.forEach((sub) => {
        (sub.submissions || []).forEach((s) => {
          const bodyText = (s.text || "").trim()
            || (s.text_html || "").replace(/<[^>]+>/g, " ");
          flat.push({
            subreddit: sub.subreddit,
            submission_id: s.submission_id || s.name || "",
            title: s.submission || s.title || "",
            text: bodyText.replace(/\s+/g, " ").trim(),
            comments: flattenComments(s.comments).join(" || ").replace(/\s+/g, " ").trim(),
            author: s.author || "",
            created_utc: s.created_utc
              ? new Date(s.created_utc * 1000).toISOString()
              : "",
            url: s.url || "",
            score: s.score ?? "",
            confidence: s.confidence ?? "",
            prediction: s.prediction ?? "",
          });
        });
      });
      const cols = [
        { label: "subreddit", get: (r) => r.subreddit },
        { label: "submission_id", get: (r) => r.submission_id },
        { label: "title", get: (r) => r.title },
        { label: "text", get: (r) => r.text },
        { label: "comments", get: (r) => r.comments },
        { label: "author", get: (r) => r.author },
        { label: "created_utc", get: (r) => r.created_utc },
        { label: "url", get: (r) => r.url },
        { label: "score", get: (r) => r.score },
        { label: "confidence", get: (r) => r.confidence },
        { label: "prediction", get: (r) => r.prediction },
      ];
      downloadBlob(toCsv(flat, cols), "text/csv;charset=utf-8", `${base}.csv`);
    }
    setExportAnchor(null);
  }, [filtered]);

  const visibleSubmissionsCount = filtered.reduce(
    (acc, s) => acc + (s.submissions || []).length,
    0
  );

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/results" underline="hover" color="text.secondary">
          All Results
        </MuiLink>
        <Typography variant="body2" color="text.primary">Reddit</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Reddit Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {submissionsCount.toLocaleString()} submissions collected
            {lastUpdated && (
              <Box component="span" sx={{ color: "text.disabled", ml: 1 }}>
                · updated {lastUpdated.toLocaleTimeString()}
              </Box>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value="results" exclusive size="small">
            <ToggleButton component={RouterLink} to="/reddit/tasks" value="tasks">
              Tasks
            </ToggleButton>
            <ToggleButton component={RouterLink} to="/reddit/results" value="results">
              Results
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            onClick={(e) => setExportAnchor(e.currentTarget)}
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            endIcon={<ArrowDropDownIcon fontSize="small" />}
            disabled={visibleSubmissionsCount === 0}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => exportVisible("json")}>
              <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="JSON" secondary="Full structure" />
            </MenuItem>
            <MenuItem onClick={() => exportVisible("csv")}>
              <ListItemIcon><TableViewIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="CSV" secondary="Flat, Excel-ready" />
            </MenuItem>
          </Menu>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Total"
            value={kpis.total}
            unit="submissions"
            accent={REDDIT_COLOR}
            icon={<ArticleIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Subreddits"
            value={kpis.subreddits}
            unit="in view"
            accent={REDDIT_COLOR}
            icon={<RedditIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Submissions in view"
            value={kpis.inView}
            accent={REDDIT_COLOR}
            loading={loading}
          />
        </Grid>
        {/* Avg confidence — hidden for now
        <Grid item xs={6} md={3}>
          <KpiTile
            label="Avg confidence"
            value={kpis.avgConf !== null ? `${Math.round(kpis.avgConf * 100)}%` : "—"}
            accent={REDDIT_COLOR}
            icon={<InsightsIcon fontSize="small" />}
            loading={loading}
            caption="in current page"
          />
        </Grid>
        */}
      </Grid>

      {/* Filters + Activity feed */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <FiltersPanel
            sources={subredditSources}
            onSourceToggle={toggleSource}
            search={filters.search}
            onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
            dateRange={filters.dateRange}
            onDateRangeChange={(v) => setFilters((f) => ({ ...f, dateRange: v }))}
            confidenceMin={filters.confidenceMin}
            onConfidenceChange={(v) => setFilters((f) => ({ ...f, confidenceMin: v }))}
            onReset={resetFilters}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          {/* Sort controls */}
          <Card sx={{ p: 1.5, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  {SORT_FIELDS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Direction</InputLabel>
                <Select
                  label="Direction"
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
                Page {page}
              </Typography>
            </Stack>
          </Card>

          {/* Subreddits accordions */}
          {results.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No Reddit submissions yet. Start a subreddit crawl to see results here.
              </Typography>
            </Card>
          ) : filtered.every((s) => (s.submissions || []).length === 0) ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No items match the current filters.
              </Typography>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filtered.map((subreddit) => (
                <Card
                  key={subreddit.subreddit_id || subreddit.subreddit}
                  id={subreddit.subreddit}
                  sx={{ borderLeft: `3px solid ${REDDIT_COLOR}` }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha(REDDIT_COLOR, 0.15)}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: REDDIT_COLOR }}>
                      r/{subreddit.subreddit}
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                        · {(subreddit.submissions || []).length} submissions
                      </Typography>
                    </Typography>
                  </Box>
                  <Accordion sx={{ boxShadow: "none" }} defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" color="text.secondary">
                        {subreddit.public_description || "(no description)"}
                      </Typography>
                    </AccordionSummary>
                    {(subreddit.submissions || []).map((submission) => (
                      <AccordionDetails key={submission.submission_id}>
                        <RedditSubmissionCard
                          key={submission.submission_id}
                          submission={submission}
                          confidenceFilter={Math.round(filters.confidenceMin * 100)}
                        />
                      </AccordionDetails>
                    ))}
                  </Accordion>
                </Card>
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              count={10}
              page={page}
              color="primary"
              onChange={(_, p) => setPage(p)}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RedditResults;
